import { useState, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { linkedinApi } from '../../services/api';
import './LazyMarketing.css';

const Leads = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await linkedinApi.listContacts({ limit: 100 });
      setContacts(response.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLeadStatus = (score) => {
    if (score >= 70) return 'Hot';
    if (score >= 40) return 'Warm';
    return 'Cold';
  };

  const handleScrapeProfile = async () => {
    if (!scrapeUrl) return;
    setScraping(true);
    try {
      await linkedinApi.scrapeSingle(scrapeUrl);
      await fetchContacts();
      setScrapeUrl('');
      setShowScrapeModal(false);
    } catch (error) {
      console.error('Failed to scrape profile:', error);
      alert(error.message || 'Failed to scrape profile');
    } finally {
      setScraping(false);
    }
  };

  const handleDeleteContact = async (id) => {
    if (!confirm('Delete this contact?')) return;
    try {
      await linkedinApi.deleteContact(id);
      await fetchContacts();
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  const stats = [
    { label: 'Total Leads', value: contacts.length.toString(), change: 'All contacts', changeType: 'neutral' },
    { label: 'From LinkedIn', value: contacts.filter(c => c.source === 'linkedin').length.toString(), change: 'Scraped profiles', changeType: 'neutral' },
    { label: 'From CSV', value: contacts.filter(c => c.source === 'csv').length.toString(), change: 'Imported', changeType: 'neutral' },
    { label: 'With Email', value: contacts.filter(c => c.email).length.toString(), change: 'Ready to contact', changeType: 'positive' },
  ];

  return (
    <div className="lm-page">
      <div className="lm-page-header">
        <div>
          <h1 className="lm-page-title">Lead Management</h1>
          <p className="lm-page-subtitle">LinkedIn profiles and imported contacts</p>
        </div>
        <div className="lm-header-actions">
          <Button variant="secondary" icon="upload" onClick={() => setShowImportModal(true)}>
            Import CSV
          </Button>
          <Button variant="primary" icon="linkedin" onClick={() => setShowScrapeModal(true)}>
            Scrape LinkedIn
          </Button>
        </div>
      </div>

      <div className="lm-stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>

      <div className="lm-section">
        <div className="lm-section-header">
          <h3 className="lm-section-title">All Contacts ({contacts.length})</h3>
          <Button variant="secondary" size="sm" icon="refresh" onClick={fetchContacts}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="loading-state">Loading contacts...</div>
        ) : contacts.length === 0 ? (
          <div className="empty-state">
            <Icon name="users" size={48} />
            <h3>No contacts yet</h3>
            <p>Scrape LinkedIn profiles or import a CSV to get started</p>
          </div>
        ) : (
          <div className="contacts-table">
            <div className="contacts-table-header">
              <span>Name</span>
              <span>Company</span>
              <span>Title</span>
              <span>Email</span>
              <span>Source</span>
              <span>Actions</span>
            </div>
            {contacts.map((contact) => (
              <div key={contact.id} className="contacts-table-row">
                <div className="contact-name">
                  <span className="name">{contact.first_name} {contact.last_name}</span>
                  {contact.linkedin_url && (
                    <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="linkedin-link">
                      <Icon name="linkedin" size={14} />
                    </a>
                  )}
                </div>
                <span className="contact-company">{contact.company || '-'}</span>
                <span className="contact-title">{contact.title || '-'}</span>
                <span className="contact-email">{contact.email || '-'}</span>
                <span className="contact-source" style={{
                  background: contact.source === 'linkedin' ? 'var(--primary-soft)' : 'var(--bg-tertiary)',
                  color: contact.source === 'linkedin' ? 'var(--primary)' : 'var(--text-secondary)'
                }}>
                  {contact.source}
                </span>
                <div className="contact-actions">
                  <button className="action-btn danger" onClick={() => handleDeleteContact(contact.id)}>
                    <Icon name="trash-2" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scrape LinkedIn Modal */}
      {showScrapeModal && (
        <div className="modal-overlay" onClick={() => setShowScrapeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Scrape LinkedIn Profile</h3>
              <button className="modal-close" onClick={() => setShowScrapeModal(false)}>
                <Icon name="x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>LinkedIn Profile URL</label>
                <input
                  type="text"
                  placeholder="https://linkedin.com/in/username"
                  value={scrapeUrl}
                  onChange={(e) => setScrapeUrl(e.target.value)}
                />
                <span className="form-hint">Enter a LinkedIn profile URL to extract contact data</span>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowScrapeModal(false)} disabled={scraping}>Cancel</Button>
              <Button variant="primary" onClick={handleScrapeProfile} disabled={scraping || !scrapeUrl}>
                {scraping ? 'Scraping...' : 'Scrape Profile'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Contacts from CSV</h3>
              <button className="modal-close" onClick={() => setShowImportModal(false)}>
                <Icon name="x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>CSV File</label>
                <div className="file-upload">
                  <input type="file" accept=".csv" />
                  <span>Drop CSV file here or click to browse</span>
                </div>
                <span className="form-hint">Required columns: email, first_name, last_name. Optional: company, title, linkedin_url</span>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowImportModal(false)}>Cancel</Button>
              <Button variant="primary">Import</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
